/**
 * Client-side helpers for the persistent task-export job pipeline.
 *
 * Mirrors `./deferredAnalysisService.ts` in shape:
 *  - a deterministic jobId derived from route params
 *  - an `onSnapshot` subscription that survives navigation / tab close
 *  - callable wrappers for `startExportJob` and `getExportDownloadUrl`
 *  - a pure progress-text formatter for inline UI status
 */

import { fromFirestoreDoc } from "@/lib/firestoreDoc";
import { db, functions, storage } from "@/lib/firebase";
import { doc, onSnapshot, type FirestoreError, type Unsubscribe } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref, uploadBytesResumable } from "firebase/storage";
import {
  buildExportJobId,
  exportInputsSizeError,
  exportInputsUploadPath,
  STALE_INITIALIZING_EXPORT_JOB_MS,
  STALE_QUEUED_EXPORT_JOB_MS,
  STALE_RUNNING_EXPORT_JOB_MS,
  type ExportJobFailureKind,
  type ExportJobPhase,
  type ExportJobStatus,
} from "@shared/exportJob";

export type { ExportJobFailureKind, ExportJobPhase };

// Re-exported so UI components import every export-job helper from this one
// service module rather than reaching into @shared directly.
export { isExportJobExpired } from "@shared/exportJob";

const START_EXPORT_CALLABLE_TIMEOUT_MS = 120_000;
const GET_EXPORT_URL_CALLABLE_TIMEOUT_MS = 30_000;

export interface ExportJobSnapshot {
  id: string;
  runId?: string | null;
  status?: ExportJobStatus;
  active?: boolean;
  phase?: ExportJobPhase | null;
  processed?: number;
  total?: number;
  filename?: string | null;
  zipBytes?: number | null;
  lastError?: string | null;
  // Tells the UI which canned suggestion to render under failed exports
  // ("timeout" → uncheck-media suggestions; "unknown" → generic + support).
  failureKind?: ExportJobFailureKind | null;
  // Firestore Timestamp from the worker on completion. Used by the UI to
  // render "exported on <date>" in the use-existing prompt.
  completedAt?: { toDate?: () => Date } | null;
  // Firestore Timestamp of the most recent server-side write. Used by
  // `isExportJobLikelyStuck` to detect frozen progress (watchdog missed,
  // worker crashed, etc.) and surface a Retry affordance to the user.
  updatedAt?: { toMillis?: () => number } | null;
  // Firestore Timestamp = completedAt + 24h (EXPORT_TTL_MS), written by the
  // worker on completion. Read-only on the client; gates the download
  // affordance via `isExportJobExpired` so we don't advertise a ZIP the
  // storage lifecycle rule has already deleted. Already present at runtime via
  // `fromFirestoreDoc` in `subscribeToExportJob`, which normalizes it to a
  // real Timestamp regardless of which shape Firestore returned.
  expiresAt?: { toMillis?: () => number } | null;
}

/**
 * The full set of export inputs. These are uploaded to Cloud Storage rather
 * than sent inline — see `uploadExportInputs`.
 */
export interface ExportInputs {
  orgId: string;
  trialId: string;
  experimentId: string;
  taskId: string;
  rootFolder: string;
  /** Write entries at the ZIP root; see ExportZipRequest.flatLayout in functions/src/lib/exportZipBuilder.ts */
  flatLayout?: boolean;
  zipFilename: string;
  textFiles: Array<{ zipPath: string; content: string }>;
  mediaFiles: Array<{ zipPath: string; storagePath: string; required?: boolean; captureId?: string }>;
  annotatedImages: Array<{
    zipPath: string;
    imgStoragePath: string;
    metadataStoragePath: string;
    entryType?: string;
    annotationClassNames?: string[];
    annotationClassColorOrder?: string[];
  }>;
  combinedAudio: Array<{ zipPath: string; audioStoragePaths: string[] }>;
}

/**
 * What actually rides on the callable: a couple of ids and a pointer to the
 * uploaded inputs. Kept tiny so the request can never approach Cloud Run's
 * 32 MiB cap.
 */
export interface StartExportJobRequest {
  orgId: string;
  inputsUploadId: string;
}

export interface StartExportJobResponse {
  jobId: string;
  runId: string;
  status: ExportJobStatus;
  reusedExistingJob: boolean;
}

export interface GetExportDownloadUrlResponse {
  downloadUrl: string;
  filename: string;
}

export function getExportJobId({
  trialId,
  experimentId,
  taskId,
}: {
  trialId: string;
  experimentId: string;
  taskId: string;
}): string {
  return buildExportJobId({ trialId, experimentId, taskId });
}

export function isExportJobSnapshotActive(job: ExportJobSnapshot | null | undefined): boolean {
  if (!job?.active) return false;
  return job.status === "initializing" || job.status === "queued" || job.status === "running";
}

export function getExportJobProgressText(job: ExportJobSnapshot | null | undefined): string | null {
  // Failure path takes precedence over the "active" check so the inline
  // status text under the Download button surfaces failures (the user
  // would otherwise see no text for a terminal failed job).
  if (job?.status === "failed") {
    if (job.failureKind === "timeout") {
      return "Last export timed out — click Download for options";
    }
    if (job.failureKind === "capture-download" && typeof job.lastError === "string" && job.lastError) {
      return "Last export incomplete — click Download for details";
    }
    return "Last export failed — click Download to retry";
  }

  if (!isExportJobSnapshotActive(job)) return null;

  const phase = job?.phase;
  const processed = Number(job?.processed ?? 0);
  const total = Number(job?.total ?? 0);

  if (!phase || phase === "text") {
    return "Preparing export...";
  }
  if (phase === "finalizing") {
    return "Finalizing ZIP...";
  }

  const safeTotal = Number.isFinite(total) && total > 0 ? total : 0;
  const safeProcessed = Number.isFinite(processed) && processed > 0 ? processed : 0;
  const percent = safeTotal > 0 ? Math.min(100, Math.round((safeProcessed / safeTotal) * 100)) : 0;

  switch (phase) {
    case "media":
      return `Exporting media: ${safeProcessed} of ${safeTotal} (${percent}%)`;
    case "annotated":
      return `Rendering annotated images: ${safeProcessed} of ${safeTotal} (${percent}%)`;
    case "audio":
      return `Compiling audio: ${safeProcessed} of ${safeTotal} (${percent}%)`;
    default:
      return "Preparing export...";
  }
}

/**
 * True when an active export job has gone quiet (no progress writes for
 * longer than the server-side stale-running window). Used by the UI to
 * surface an "Export appears stuck — retry" affordance when the watchdog
 * misses or the worker crashes silently.
 *
 * Returns false on null/terminal/no-updatedAt jobs (don't assume stuck
 * without evidence).
 */
export function isExportJobLikelyStuck(job: ExportJobSnapshot | null | undefined, nowMs: number): boolean {
  if (!isExportJobSnapshotActive(job)) return false;
  const updatedAtMs = job?.updatedAt?.toMillis?.();
  if (typeof updatedAtMs !== "number") return false;
  // Mirror the server's per-status reclaim thresholds in
  // `shared/exportJob.ts` (decideExportJobClaim) so the Retry UI surfaces
  // exactly when the server is willing to hand out a new runId.
  const thresholdMs =
    job?.status === "initializing"
      ? STALE_INITIALIZING_EXPORT_JOB_MS
      : job?.status === "queued"
        ? STALE_QUEUED_EXPORT_JOB_MS
        : STALE_RUNNING_EXPORT_JOB_MS;
  return nowMs - updatedAtMs > thresholdMs;
}

export function buildOptimisticExportJobSnapshot(response: StartExportJobResponse): ExportJobSnapshot | null {
  if (response.reusedExistingJob) {
    // Existing job — rely on the live onSnapshot subscription to deliver real
    // state. Avoid overwriting current progress with a 0/0 placeholder.
    return null;
  }
  const active = response.status === "initializing" || response.status === "queued" || response.status === "running";
  return {
    id: response.jobId,
    runId: response.runId,
    status: response.status,
    active,
    phase: null,
    processed: 0,
    total: 0,
  };
}

export function subscribeToExportJob(
  orgId: string,
  jobId: string,
  onChange: (job: ExportJobSnapshot | null) => void,
  onError?: (error: FirestoreError) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, `organizations/${orgId}/exportJobs/${jobId}`),
    (snapshot) => {
      onChange(snapshot.exists() ? fromFirestoreDoc<ExportJobSnapshot>(snapshot) : null);
    },
    (error) => {
      onError?.(error);
    }
  );
}

/**
 * Mint an id for one export-inputs upload. `startExportJob` accepts nothing
 * but a plain UUID here, so a pointer can never escape the org's prefix.
 */
export function newExportInputsUploadId(): string {
  return crypto.randomUUID();
}

/**
 * Upload the export inputs to Cloud Storage, so only a pointer rides on the
 * callable. See shared/exportJob.ts for why.
 *
 * The `application/json` content type is required by the storage rule, not
 * cosmetic.
 */
export async function uploadExportInputs(
  orgId: string,
  uploadId: string,
  payload: ExportInputs
): Promise<void> {
  const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
  // The storage rule would reject an oversized upload as a bare
  // `storage/unauthorized`, which says nothing about size.
  const sizeError = exportInputsSizeError(blob.size);
  if (sizeError) throw new Error(sizeError);
  // Resumable, not uploadBytes: these payloads run to tens of MB and field
  // users upload them over rural connections, where a single non-resumable
  // multipart request restarts from zero on any interruption.
  await uploadBytesResumable(ref(storage, exportInputsUploadPath(orgId, uploadId)), blob, {
    contentType: "application/json",
  });
}

export async function startExportJob(request: StartExportJobRequest): Promise<StartExportJobResponse> {
  const callable = httpsCallable<StartExportJobRequest, StartExportJobResponse>(functions, "startExportJob", {
    timeout: START_EXPORT_CALLABLE_TIMEOUT_MS,
  });
  const result = await callable(request);
  return result.data;
}

export async function getExportDownloadUrl(args: {
  orgId: string;
  jobId: string;
}): Promise<GetExportDownloadUrlResponse> {
  const callable = httpsCallable<typeof args, GetExportDownloadUrlResponse>(functions, "getExportDownloadUrl", {
    timeout: GET_EXPORT_URL_CALLABLE_TIMEOUT_MS,
  });
  const result = await callable(args);
  return result.data;
}
